import { useCallback, useEffect, useState } from 'react';
import { AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import styled from 'styled-components';
import { Scrollbars } from 'react-custom-scrollbars';
import axios from 'axios';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import { axiosPath } from '../../../../../../routing/helpers/router';
import colors from '../../../../../../constants/colors';
import Translator from '../../../../../components/helpers/Translator';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import errorHandler from '../../../../../components/error/ErrorHandler';
import EvaluationHome from './EvaluationHome';
import EvaluationSidebarElement from './EvaluationSidebarElement';
import minions from '@/minions/minions';
import { getEvaluationName } from '../../tools/environmentalAspectsAssessmentTools';

export default function EvaluationTab({ inspectionID }) {
  const { moduleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((s) => s?.module?.[moduleBridgeHex]?.moduleData);
  const inspection = useSelector((state) =>
    state.currentPage?.inspections.find((i) => i?.inspectionID === inspectionID),
  );
  const [evaluations, setEvaluations] = useState();
  const [selectedEvaluation, setSelectedEvaluation] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [showAddNewEvaluation, setShowAddNewEvaluation] = useState();
  const [isEditMode, setIsEditMode] = useState();
  const [evaluationOptions] = useState([
    {
      value: 0,
      label: 'NA',
    },
    { value: 1, label: 'Correcto' },
    { value: 2, label: 'Incorrecto' },
  ]);
  const validationSchema = Yup.object().shape({
    evaluation: Yup.number()
      .nullable()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="user" name="user.required" />,
        ),
      ),
    evaluationDateStamp: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />),
    ),
    nonConformityCode: Yup.string().when('evaluation', {
      is: 2,
      then: (schema) =>
        schema.required(
          ReactDOMServer.renderToString(
            <Translator file="user" name="user.required" />,
          ),
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  // > LOAD EVALUATIONS
  const loadEvaluations = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_environmentalAspectsAssessment_inspection_evaluation_list',
          ),
          {
            moduleBridgeHex,
            plantHex,
            assessmentID: moduleData?.assessmentID,
            inspectionID,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setEvaluations(data);
            setSelectedEvaluation();
            setShowAddNewEvaluation(false);
            setIsEditMode(false);
            setIsLoading(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, plantHex, moduleData, inspectionID],
  );

  useEffect(() => {
    loadEvaluations();
  }, []);
  // < LOAD EVALUATIONS

  // > ADD EVALUATION
  const handleAdd = useCallback(
    async (values) => {
      setIsLoading(true);
      axios
        .post(
          axiosPath(
            'api_r_user_module_environmentalAspectsAssessment_inspection_evaluation_add',
          ),
          minions.forms.toFormData({
            moduleBridgeHex,
            plantHex,
            inspectionID,
            assessmentID: moduleData?.assessmentID,
            ...values,
          }),
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        .then(() => {
          if (isMounted.current) {
            loadEvaluations();
          }
        })
        .catch((e) => dispatch(errorHandler(e)));
    },
    [moduleBridgeHex, moduleData, inspectionID],
  );
  // < ADD EVALUATION

  // > EDIT EVALUATION
  const handleEdit = useCallback(
    async (values) => {
      setIsLoading(true);
      axios
        .post(
          axiosPath(
            'api_r_user_module_environmentalAspectsAssessment_inspection_evaluation_edit',
          ),
          minions.forms.toFormData({
            moduleBridgeHex,
            plantHex,
            inspectionID,
            assessmentID: moduleData?.assessmentID,
            ...values,
          }),
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        .then(() => {
          if (isMounted.current) {
            loadEvaluations();
          }
        })
        .catch((e) => dispatch(errorHandler(e)));
    },
    [moduleBridgeHex, moduleData, inspectionID],
  );
  // < EDIT EVALUATION

  // > DOWNLOAD FILE
  const handleDownloadFile = async (salt) =>
    axios
      .post(
        axiosPath(
          'api_r_user_module_environmentalAspectsAssessment_inspection_evaluation_download',
        ),
        {
          moduleBridgeHex,
          plantHex,
          inspectionID,
          assessmentID: moduleData?.assessmentID,
          evaluationID: selectedEvaluation.id,
          salt,
        },
      )
      .then(({ data }) => window.open(data, '_blank'))
      .catch((e) => dispatch(errorHandler(e)));
  // < DOWNLOAD FILE

  if (!evaluations) return <AppNoInfo minHeight={400} />;

  return (
    <Container className="pt-3 border-top-0 bg-white">
      <SidebarMenu>
        <>
          <span
            className="add-button"
            onClick={() => {
              setShowAddNewEvaluation(true);
              setSelectedEvaluation(false);
              setIsEditMode(false);
            }}
          >
            Nueva evaluación
          </span>
          {evaluations?.length > 0 ? (
            <Scrollbars style={{ height: '365px' }}>
              <ul>
                {evaluations.map((e) => (
                  <li
                    key={e.id}
                    className={`evaluation${
                      selectedEvaluation && selectedEvaluation.id === e.id
                        ? ' ev-selected'
                        : ''
                    }`}
                    style={{
                      borderLeftColor:
                        e?.evaluation === 1
                          ? 'green'
                          : e?.evaluation === 2
                            ? 'red'
                            : colors.grey400,
                    }}
                    onClick={() => {
                      setShowAddNewEvaluation(false);
                      setSelectedEvaluation(e);
                      setIsEditMode(false);
                    }}
                  >
                    <EvaluationSidebarElement evaluation={e} />
                  </li>
                ))}
              </ul>
            </Scrollbars>
          ) : (
            <AppNoInfo
              text="No hay evaluaciones"
              color={colors.primaryLight}
              icon={
                <AppRemixIcon
                  size={24}
                  icon="file-search"
                  color={colors.primaryLight}
                />
              }
            />
          )}
        </>
      </SidebarMenu>
      <Content>
        {showAddNewEvaluation ? (
          <EvaluationHome
            inspection={inspection}
            evaluationOptions={evaluationOptions}
            validationSchema={validationSchema}
            onSave={handleAdd}
            // isLoading={isLoading}
          />
        ) : (
          <>
            {selectedEvaluation ? (
              <>
                {isEditMode ? (
                  <EvaluationHome
                    inspection={inspection}
                    evaluationOptions={evaluationOptions}
                    validationSchema={validationSchema}
                    selectedEvaluation={selectedEvaluation}
                    onSave={handleEdit}
                    onCancel={() => {
                      setSelectedEvaluation(null);
                      setIsEditMode(false);
                    }}
                    // onRemove={handleRemove}
                    // isLoading={isLoading}
                  />
                ) : (
                  <>
                    <h3>
                      Información de la evaluación
                      <span
                        className="float-right pointer mr-2"
                        title={ReactDOMServer.renderToString(
                          <Translator file="module-legalCompliance" name="close" />,
                        )}
                      >
                        <AppRemixIcon
                          icon="close"
                          size={16}
                          onClick={() => {
                            setSelectedEvaluation(null);
                            setIsEditMode(false);
                          }}
                        />
                      </span>
                      <span
                        className="float-right pointer mr-2"
                        title={ReactDOMServer.renderToString(
                          <Translator file="general" name="general.edit" />,
                        )}
                      >
                        <AppRemixIcon
                          icon="edit"
                          className="float-right pointer mr-2"
                          size={16}
                          onClick={() => setIsEditMode(true)}
                        />
                      </span>
                    </h3>
                    <EvaluationInfo className="mt-4">
                      <li>
                        <span className="eventinfo-title">
                          Criterio de aceptación
                        </span>
                        <span className="eventinfo-content">
                          {selectedEvaluation.acceptanceCriteria}
                        </span>
                      </li>
                      <li>
                        <span className="eventinfo-title">Evaluación</span>
                        <span className="eventinfo-content">
                          {getEvaluationName(selectedEvaluation.evaluation)}
                        </span>
                      </li>
                      <li>
                        <span className="eventinfo-title">
                          <Translator
                            file="user"
                            name="user.obligation.evidence.date"
                          />
                        </span>
                        <span className="eventinfo-content">
                          {selectedEvaluation.evaluationDateStamp ? (
                            selectedEvaluation.evaluationDateStamp
                          ) : (
                            <span className="text-muted">
                              <Translator file="user" name="user.no-date" />
                            </span>
                          )}
                        </span>
                      </li>
                      <li>
                        <span className="eventinfo-title">Código no conformidad</span>
                        <span className="eventinfo-content">
                          {selectedEvaluation.nonConformityCode}
                        </span>
                      </li>
                      <li>
                        <span className="eventinfo-title">Comentario</span>
                        <span
                          className="eventinfo-content"
                          style={{ maxWidth: 'calc(100vw - 900px)' }}
                        >
                          {selectedEvaluation.comment}
                        </span>
                      </li>
                      <li>
                        <span className="eventinfo-title">
                          <Translator
                            file="module-legalCompliance"
                            name="associated.files"
                          />
                        </span>
                        <span className="eventinfo-content">
                          {selectedEvaluation.files &&
                          Object.entries(selectedEvaluation.files).length > 0 ? (
                            Object.entries(selectedEvaluation.files).map(
                              ([salt, file]) => (
                                <div
                                  className="pointer mb-1"
                                  key={salt}
                                  onClick={() => handleDownloadFile(salt)}
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-legalCompliance"
                                      name="download.file"
                                    />,
                                  )}
                                >
                                  {minions.files.fileIcon(
                                    file?.name,
                                    true,
                                    null,
                                    'mr-2',
                                  )}
                                  {file?.name}
                                </div>
                              ),
                            )
                          ) : (
                            <span>
                              <Translator
                                file="module-legalCompliance"
                                name="no.associated.files"
                              />
                            </span>
                          )}
                        </span>
                      </li>
                    </EvaluationInfo>
                  </>
                )}
              </>
            ) : (
              <AppNoInfo
                text={
                  <>
                    <span>
                      Selecciona una evaluación de la lista o crea una nueva pulsando
                      sobre el botón <b>Nueva evaluación</b>.
                    </span>
                    <br />
                  </>
                }
                color={colors.primaryLight}
                icon={
                  <AppRemixIcon
                    size={40}
                    icon="file-search"
                    color={colors.primaryLight}
                  />
                }
              />
            )}
          </>
        )}
      </Content>
    </Container>
  );
}

const Container = styled.div`
  height: 420px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: 350px 1fr;
`;

const SidebarMenu = styled.div`
  overflow-y: auto;
  padding: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  .add-button {
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid ${colors.primary};
    color: ${colors.primary};
    margin: 0;
    height: 32px;
    cursor: pointer;
  }
  ul {
    padding: 4px;
    overflow-y: auto;
    .evaluation {
      padding: 4px 2px;
      border-bottom: 1px solid ${colors.grey300};
      border-left: 3px solid;
      cursor: pointer;
      &.ev-selected {
        background: ${colors.grey300};
      }
      .first-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: ${colors.primary};
        .name {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      .second-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 10px;
        .sr-date {
          letter-spacing: 1px;
        }
        .sr-user {
          color: ${colors.primary};
        }
      }
    }
  }
`;

const Content = styled.div`
  border-left: 1px solid ${colors.grey300};
  padding: 4px;
  height: 400px;
  .row-no-margin > .row {
    margin: 0;
  }
  h3 {
    font-size: 13px;
    font-weight: bold;
  }
`;

const EvaluationInfo = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  height: 350px;
  overflow: auto;
  li {
    padding: 8px 0;
    border-top: 1px solid ${colors.grey300};
    display: flex;
    align-items: baseline;
    .eventinfo-title {
      display: inline-block;
      width: 200px;
      text-transform: uppercase;
      font-weight: bold;
      color: ${colors.primary};
    }
  }
`;
